import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { DatesProvider, MonthPicker } from "@mantine/dates";
import clsx from "clsx";
import type { FunctionComponent } from "react";

import type { QuickSelectOption, TempDateRange } from "../useDeliveryPeriodPicker.ts";

interface DeliveryPeriodPickerPopoverProps {
	isOpen: boolean;
	context: Parameters<typeof FloatingFocusManager>[0]["context"];
	refs: {
		setFloating: (node: HTMLElement | null) => void;
	};
	floatingStyles: React.CSSProperties;
	getFloatingProps: () => Record<string, unknown>;
	tempDateRange: TempDateRange;
	onRangeChange: (value: TempDateRange) => void;
	quickSelectOptions: QuickSelectOption[];
	activeQuickSelect: string | null;
	now: Date;
}

const DeliveryPeriodPickerPopover: FunctionComponent<DeliveryPeriodPickerPopoverProps> = ({
	isOpen,
	context,
	refs,
	floatingStyles,
	getFloatingProps,
	tempDateRange,
	onRangeChange,
	quickSelectOptions,
	activeQuickSelect,
	now,
}) => {
	if (!isOpen) return null;

	return (
		<FloatingPortal>
			<FloatingFocusManager context={context}
								  modal={false}>
				<div
					ref={refs.setFloating}
					style={floatingStyles}
					className="z-50 rounded-md bg-white px-3 py-4 shadow-lg ring-1 ring-black/5"
					{...getFloatingProps()}
				>
					<div className="w-72">
						<DatesProvider settings={{ locale: "de" }}>
							<MonthPicker
								type="range"
								value={tempDateRange}
								onChange={onRangeChange}
								classNames={{
									monthsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
									  data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300
									  data-[selected]:bg-accent-500 data-[in-range]:bg-primary-200`,
									monthsList: "w-full",
									calendarHeader: "flex justify-between h-10 bg-white",
									calendarHeaderLevel: "flex gap-1 justify-center items-center hover:bg-gray-100 rounded-full px-4",
									calendarHeaderControl: "flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full data-[disabled]:opacity-0",
									calendarHeaderControlIcon: "text-gray-400 data-[direction='previous']:rotate-90 data-[direction='next']:-rotate-90",
									yearsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
									  data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300`,
									yearsList: "w-full",
								}}
							/>
						</DatesProvider>
						<div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
							{quickSelectOptions.map((opt) => (
								<button
									key={opt.label}
									type="button"
									onClick={() => onRangeChange([opt.start, now])}
									className={clsx(
										"rounded-full px-3 py-1 text-xs font-medium transition-colors",
										activeQuickSelect === opt.label
											? "bg-primary-500 text-white"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200",
									)}
								>
									{opt.label}
								</button>
							))}
						</div>
					</div>
				</div>
			</FloatingFocusManager>
		</FloatingPortal>
	);
};

DeliveryPeriodPickerPopover.displayName = "DeliveryPeriodPickerPopover";

export default DeliveryPeriodPickerPopover;